import React, { ReactNode } from 'react';

import { Form } from 'react-bootstrap';

import { mergeClassNameStrings } from '@consumer-frontend/nc-utils';

import { RadioButton, RadioButtonPropsT } from '../RadioButton';

export type RadioGroupPropsT<RadioButtonProps> = {
  controlId: string; // also used as `name` for the radio buttons
  children?: ReactNode;
  className?: string;
  errorMessage?: ReactNode;
  groupError?: boolean;
  helperText?: ReactNode;
  label?: ReactNode;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  options?: Partial<RadioButtonProps>[];
  RadioButtonComponent?: React.ComponentType<RadioButtonProps>;
  ref?: React.Ref<HTMLInputElement>;
  value?: string | number;
};
export type RadioGroupT<RadioButtonProps = RadioButtonPropsT> = React.FC<RadioGroupPropsT<RadioButtonProps>>;

export const RadioGroup: RadioGroupT = ({
  children,
  className,
  controlId,
  errorMessage,
  groupError,
  helperText,
  label,
  onChange,
  options,
  RadioButtonComponent = RadioButton,
  ref,
  value,
}) => {
  const cssClasses = mergeClassNameStrings('form-group radio-group', className);
  const isInvalid = !!errorMessage;
  const helperId = helperText ? `${controlId}-helper` : undefined;
  const errorId = isInvalid ? `${controlId}-error` : undefined;
  const describedBy = [helperId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <Form.Group
      className={cssClasses}
      controlId={controlId}
      aria-describedby={describedBy}
      aria-invalid={isInvalid}
    >
      {ref && (
        <span
          ref={ref}
          aria-hidden="true"
          className="visually-hidden"
          tabIndex={-1}
        />
      )}
      {label && <Form.Label className="radio-group-label">{label}</Form.Label>}

      {helperText && <p id={helperId} className="helper-text">{helperText}</p>}

      {children || options?.map((option) => (
        <RadioButtonComponent
          checked={option.value === value}
          className={option.className}
          disabled={option.disabled}
          errorMessage={errorMessage}
          groupError={groupError}
          id={`${controlId}-${option.value}`}
          key={option.value}
          label={option.label}
          name={controlId}
          onChange={onChange}
          value={option.value}
        />
      ))}

      {errorMessage && (
        <Form.Control.Feedback type="invalid" id={errorId}>
          {errorMessage}
        </Form.Control.Feedback>
      )}
    </Form.Group>
  );
};
